import React from 'react';
import "./index.css";

export default function Help(props) {
  return (
    <div className="help">
      <h1>Taptivate Help</h1>
      <h3>Getting Started with Taptivate</h3>
      <p><span>Taptivate is an all-in-one marketing platform that helps you Create Campaigns, Design Landing Pages and Design and Order NFC Tags. Our approach starts with beautifully designed Campaigns and powerful data analysis. </span></p>
      <p><span>In this article, you&#39;ll learn how to start using Taptivate to create awesome campaigns, and we&#39;ll provide some helpful resources to use as you work.</span></p>
      <h3>Things to know</h3>
      <p><span>Before you jump into the tasks ahead, here are a few helpful things to know.</span></p>
      <ul>
        <li>
          <p><span>Taptivate is a web-based application that works in most web browsers, which means you don&#39;t need to download or install any software on your computer. To be sure Taptivate works properly in your browser, you should enable cookies, pop-ups, and JavaScript.</span></p>
        </li>
        <li>
          <p><span>It&#39;s important to note that when you create a Taptivate account, you agree to comply with our </span><span><a href="/tos">Terms of Use</a></span><span>.</span></p>
        </li>
        <li>
          <p><span>We offer a number of online resources to provide help and insights about our tools and features. More to come&hellip;.</span></p>
        </li>
      </ul>
      <h3>HELP Overview</h3>
      <ul>
        <li>
          <p><span>Set up your account</span></p>
        </li>
        <li><span>Company and User Settings</span></li>
        <li>
          <p><span>Media Library</span></p>
        </li>
        <li>
          <p><span>Taptivate Terminology</span></p>
        </li>
        <li>
          <p><span>Create a campaign</span></p>
        </li>
        <li><span>Design a landing page</span></li>
        <li><span>Design an NFC tag</span></li>
        <li><span>Preview and Publish your campaign</span></li>
        <li><span>Create landing page and NFC Tag templates</span></li>
        <li>
          <p><span>View your campaign reports</span></p>
        </li>
      </ul>
      <h3>Set up your account</h3>
      <p><span>When you sign up for a Taptivate account, you&#39;ll enter your name and email address, and we&#39;ll send you an activation email. When you receive the activation email, click the link to activate your account.</span></p>
      <p><span>The next time you log in to our website, we&#39;ll walk you through the rest of the setup steps. You&#39;ll input profile details, choose a monthly plan and enter credit card information to set up monthly payments.</span></p>
      <p><span>To learn more about account creation, read the article </span><span><a href="/auth/register">Create an Account</a></span><span>.</span></p>
      <h3>Company Setting and User Setting</h3>
      <ul>
        <li>
          <p><span>View/Edit your company settings </span></p>
        </li>
        <li>
          <p><span>Invite contributors (seats are based on plan)</span></p>
        </li>
        <li><span>Add Google Analytics code</span></li>
        <li><span>Change password, email and username</span></li>
      </ul>
      <h3>Taptivate Terminology</h3>
      <h5>Campaigns</h5>
      <p><span>A campaign consists of campaign data (name, date range, location etc), a landing page, and an NFC tag that we print and ship for you to attach to your customer packages, hang tags or anything you share with customers. </span></p>
      <p><span>Example campaign page</span></p>
      <p><span><img alt="" src={require("./images/image5.png")} title=""/></span></p>
      <p><span>Your monthly, quarterly or seasonal campaigns will connect your customer when they &ldquo;tap&rdquo; an NFC tag and are linked to your landing page. &nbsp;Your campaigns can include any marketing message or sales promotions, include video or direct users to your social media. It&rsquo;s your marketing calendar brought to life through the powerful simplicity of an NFC Tag and it&rsquo;s connection to your landing page.</span></p>
      <h5>Taptivate NFC tags</h5>
      <p><span>Taptivate tags can be integrated into a wide variety of packages and easily read with NFC-enabled smartphones. You can create a custom design and campaign message using our tag designer or upload your own design.</span></p>
      <h5>Landing pages</h5>
      <p><span>A landing page is a single web page that is launched on NFC enabled smartphones when your customer taps an NFC tag. Landing pages can be custom designed for your campaign with your brand identity, imagery and messaging and provide your customer with links to other websites or social networks.</span></p>
      <h5>Landing page and tag templates</h5>
      <p><span>A template is a preset layout that you build to use as a starting place for landing pages and tags. </span></p>
      <p><span>There are many </span><span><a href="/tempates">template options</a></span><span>&nbsp;available in Taptivate, but you can also </span><span><a href="https://www.google.com/url?q=https://mailchimp.com/help/import-a-custom-html-template/&amp;sa=D&amp;ust=1584937669508000">create your own</a></span><span>&nbsp;template to match your brand. You can even insert regularly used images, logos, or recurring content into your template, and reuse it for future campaigns.</span></p>
      <h5>What is NFC?</h5>
      <p><span>NFC stands for near-field communication and it allows phones, tablets, laptops, and other devices to easily share data with other NFC-equipped devices. It evolved from radio-frequency identification (RFID) technology. RFID is behind those security scan cards that get you into the office every day or bypass that tollbooth on your morning commute.</span></p>
      <p><span>NFC is very much like RFID, but NFC is limited to communication within about four inches, which is why you have to hold your phone so close to the contactless reader if you&rsquo;re using Apple Pay or Samsung Pay. Most people consider NFC&rsquo;s small radius a major security benefit, and it&rsquo;s one of the reasons that NFC has taken off as a secure alternative to credit cards. The technology can be used for more than buying coffee at Starbucks, however. NFC can also transfer data like videos, contact information, and photos between two NFC-enabled devices.</span></p>
      <p><span>We live in an Experience economy, and Near Field Communication (NFC) technology makes it easier than ever for brands and retailers to dimensionalize the experiences their customers have with products and services. In particular, Taptivate&#39;s NFC tags offer brands engaged in e-commerce a powerful new way to create and enhance the excitement and anticipation associated with ordering, receiving, and opening a delivered box or package.</span></p>
      <h3>Media Library</h3>
      <p><span>The media library is the place to upload artwork, images and logos to use in your designs. You can access the media library from our landing page and tag design pages. Below is a list of recommended specifications for each type of media.</span></p>
      <h5>Logos</h5>
      <p><span>PNGs and JPGs are the best formats. Sizing depends on landing page and tag design and can be scaled in the designer editors for each.</span></p>
      <h5>Photographs</h5>
      <p><span>JPGs are the best format. Sizing depends on landing page and tag design and can be scaled in the designer editors for each.</span></p>
      <h5>Your Tag Designs</h5>
      <p><span>PNGs and JPGs are the best formats. PDF will be offered in the next version. We will also offer templates to upload your own design in the next version. Templates for designing your own tag will be provided in file formats including InDesign, Photoshop and Illustrator.</span></p>
      <h3>Custom Shapes and Sized Tags</h3>
      <p><span>Customize your NFC tag with your brand&rsquo;s look and feel. Your logo, your color, your images. Also, choose from an array of shapes and sizes or forward us your customized shape and we&rsquo;ll bring it to life.</span></p>
      <p><span>To customize the shape of your tag please contact us at info@taptivate.co.</span></p>
      <h3>Create a campaign</h3>
      <p><span>First, you&#39;ll craft your first campaign. A campaign is a marketing message that you share via NFC tag and landing page content. </span></p>
      <p><span>To create a campaign, follow these steps.</span></p>
      <ol>
        <li>
          <span>From the dashboard select Start a Campaign.</span>
          <p><span><img alt="" src={require("./images/image10.png")} title=""/></span></p>
          <p><span>Or</span></p>
          <p><span>From the Campaign Tab select Create Campaign.</span></p>
          <p><span><img alt="" src={require("./images/image12.png")} title=""/></span></p>
        </li>
        <li><span>Enter a campaign name and follow the steps to add a custom url, date range and locations.</span></li>
        <li><span>You will land on a campaign page and instructed to add a landing page and a tag to complete your campaign.</span><span><img alt="" src={require("./images/image3.png")} title=""/></span></li>
        <li>
          <span>Once you have added a landing page and an NFC tag, you can publish the URL to access the live site</span>
          <p><span><img alt="" src={require("./images/image6.png")} title=""/></span></p>
        </li>
        <li>
          <span>Below is an example of a completed campaign. You can edit all parts o a campaign from this page. You can also add multiple tags to one campaign.</span>
          <p><span><img alt="" src={require("./images/image15.png")} title=""/></span></p>
        </li>
      </ol>
      <p><span>Designing a campaign in Taptivate can be fun, but also complex, because we offer a lot of customization options. Take some time to get used to the controls and always plan your campaigns before you start designing them. You&#39;ll be an expert email, ad, landing page, and postcard campaign designer before you know it.</span></p>
      <h3>Create a Landing Page</h3>
      <p><span>Taptivate&rsquo;s landing page module is simple and robust. &nbsp;Whether your a pro-marketer or a non-technical business owner, you can design beautiful, high converting landing pages with our intuitive interface. </span></p>
      <h3>Before you start</h3>
      <p><span>Here are some things to know before you begin this process.</span></p>
      <ul>
        <li>
          <p><span>Landing pages must be associated with a campaign in your account.</span></p>
        </li>
        <li>
          <p><span>The landing page builder doesn&rsquo;t prevent you from publishing default content in your layout. We strongly recommend you preview your landing page and review your settings before you publish.</span></p>
        </li>
        <li>
          <p><span>Custom HTML and CSS is supported.</span></p>
        </li>
      </ul>
      <h3>Landing page steps</h3>
      <p><span>First, you&#39;ll name your new landing page and choose a method to design. You can code your own or use our integrated </span><span><a href="https://www.google.com/url?q=https://grapesjs.com/docs/&amp;sa=D&amp;ust=1584937669516000">grapejs</a></span><span>&nbsp;builder.</span></p>
      <p><span>To create your landing page, follow these steps.</span></p>
      <ol start="1">
        <li>
          <p><span>Choose from one of the following</span></p>
          <p><span>On the Dashboard you can start a landing page template</span></p>
          <p><span><img alt="" src={require("./images/image14.png")} title=""/></span></p>
          <p><span>On a campaign page you can add a landing page to a campaign</span></p>
          <p><span><img alt="" src={require("./images/image11.png")} title=""/></span></p>
        </li>
        <li>
          <p><span>Type a name for your landing page. This name is for your reference, and isn&#39;t visible to landing page visitors.</span></p>
          <p><span><img alt="" src={require("./images/image1.png")} title=""/></span><span><br/></span></p>
        </li>
        <li>
          <p><span>You can start with a blank template, choose from one of our templates or choose from your templates if available.<br/><br/>Choose blank template<br/></span><span><img alt="" src={require("./images/image19.png")} title=""/></span></p>
          <p><span>Choose of our templates or one that you have created</span></p>
          <p><span><img alt="" src={require("./images/image16.png")} title=""/></span><span><img alt="" src={require("./images/image13.png")} title=""/></span></p>
        </li>
        <li>
          <p><span>Choose one of the following methods to begin designing:</span></p>
          <p><span><img alt="" src={require("./images/image4.png")} title=""/></span><span><br/></span></p>
        </li>
      </ol>
      <p><span>Great work! This will take you to the landing page builder.</span></p>
      <h3>Create an NFC tag</h3>
      <p><span>Design and customize your brands NFC tag. Taptivate NFC tags can be designed with your logo, message or slogan and affixed to the inside or outside of any product or package, connecting you to your customers at the Unboxing Moment. &nbsp;</span></p>
      <p><span>Choose from an array of shapes, sizes, and configurations or contact us with your unique shape.</span></p>
      <h3>Before you start</h3>
      <p><span>Here are some things to know before you begin this process.</span></p>
      <ul>
        <li>
          <p><span>NFC tags must be associated with a campaign in your account.</span></p>
        </li>
        <li>
          <p><span>There are 2 methods for designer tags. You can design your own and upload or you can use our design tools.</span></p>
        </li>
        <li>
          <p><span>Tags are ordered through Shopify and you will be directed upon checkout to the Shopify store.</span></p>
        </li>
      </ul>
      <h3>NFC tag steps</h3>
      <p><span>First, you&#39;ll name your new tag and choose a method to design. </span></p>
      <p><span>To create your NFC tag, follow these steps.</span></p>
      <ol>
        <li>
          <p><span>Choose from one of the following</span></p>
          <p><span>On the Dashboard you can start a tag template</span></p>
          <p><span><img alt="" src={require("./images/image9.png")} title=""/></span></p>
          <p><span>On a campaign page you can add a tag to a campaign</span></p>
          <p><span><img alt="" src={require("./images/image7.png")} title=""/></span></p>
        </li>
        <li>
          <p><span>Type a name for your tag. This name is for your reference, and isn&#39;t visible to landing page visitors.</span></p>
          <p>
            <span><img alt="" src={require("./images/image8.png")} title=""/></span>
          </p>
        </li>
        <li>
          <p><span>You can start with a blank template or choose from one of your templates if available</span></p>
          <p><span><img alt="" src={require("./images/image18.png")} title=""/></span></p>
        </li>
        <li>
          <p><span>Choose one of the following shapes and sizes to begin designing:</span></p>
          <p><span>Note: TAP YOUR PHONE HERE is not editable and provides clear direction for your customers to use their phone to access your marketing message</span></p>
          <p><span><img alt="" src={require("./images/image2.png")} title=""/></span></p>
        </li>
        <li>
          <p><span>Choose one of the following methods to begin designing:</span></p>
          <p><span><img alt="" src={require("./images/image17.png")} title=""/></span></p>
        </li>
      </ol>
      <p><span>Great work! This will take you to the NFC tag designer.</span></p>
      <h3>Ordering NFC tags</h3>
      <p><span>To come</span></p>
    </div>
  );
}